import { yupResolver } from '@hookform/resolvers/yup';
import { ExpandMore } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import _, { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
	AutocompleteElement,
	CheckboxButtonGroup,
	SelectElement,
	TextFieldElement,
} from 'react-hook-form-mui';
import * as yup from 'yup';
import { useAuthContext } from '../../../../../../context/auth';
import { errorToast, successToast } from '../../../../../Toast';
import { TenantsApi } from '../../../../namespace/api';
import {
	getEventTypes,
	QUERY_KEYS as EVENTS_TYPES_QUERY_KEYS,
} from '../../../api/eventTypes';
import { ExternalServiceIntegration } from '../../../api/integrations';
import {
	configureWebhook,
	QUERY_KEYS,
	WebhookRequest,
	WebhookResponse,
	WEBHOOK_FAILURE_MODE_DIVERT,
	WEBHOOK_FAILURE_MODE_NONE,
	WEBHOOK_FAILURE_MODE_SUSPEND,
	WEBHOOK_RETRY_STRATEGY_NONE,
	WEBHOOK_RETRY_STRATEGY_ONE,
	WEBHOOK_RETRY_STRATEGY_THREE,
	WEBHOOK_STATUS_CODES,
} from '../../../api/webhooks';
import {
	ACTION_CANCEL,
	ACTION_CREATE,
	ACTION_SAVE,
	DISPLAYNAME_INVALID_FORMAT,
	EDIT_LABEL,
	EMPTY_ERROR_MSG,
	INVALID_FORMAT_ERROR_MSG,
	IS_TAKEN_ERROR_MSG,
	NEW_LABEL,
	UNEDITABLE_FIELD_INFO,
	WEBHOOK_ACT_ON_STATUS_MSG,
	WEBHOOK_CONFIGURATION_ADDED_FAILURE,
	WEBHOOK_CONFIGURATION_ADDED_SUCCESS,
	WEBHOOK_CONFIGURATION_EDITED_FAILURE,
	WEBHOOK_CONFIGURATION_EDITED_SUCCESS,
	WEBHOOK_CONFIGURATION_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_ACT_ON_STATUS_CODE_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_ACT_ON_TIMEOUT_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_ALERT_INTEGRATION_NAME_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_DISPLAYNAME_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_ENABLED_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_EVENTS_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_FAILURE_HANDLING_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_FAILURE_STRATEGY_DISABLED_HELPER_TEXT,
	WEBHOOK_CONFIGURATION_MODAL_FAILURE_STRATEGY_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_INTEGRATION_NAME_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_NAME_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE,
	WEBHOOK_CONFIGURATION_MODAL_RETRY_STRATEGY_LABEL,
	WEBHOOK_INTEGRATION_ERR_MSG,
} from '../../../data/constants';
import { handleModalClose } from '../../../utils/handleModalClose';

interface Props {
	open: boolean;
	webhooks: ExternalServiceIntegration[];
	onModalClose: () => void;
	onSuccess: () => Promise<void>;
	configuredWebhook: WebhookResponse;
	configuredWebhooks: WebhookResponse[];
	tenantHostname?: string;
}

type AutoCompleteOption = {
	id: string;
	label: string;
};

const WEBHOOK_CONFIGURATION_FORM_KEYS = {
	NAME: 'name',
	DISPLAYNAME: 'displayName',
	INTEGRATION_NAME: 'integrationName',
	EVENTS: 'events',
	RETRY_STRATEGY: 'retryStrategy',
	FAILURE_HANDLING: 'failureHandling',
	FAILURE_MODE: 'mode',
	ACT_ON_TIMEOUT: 'actOnTimeout',
	ACT_ON_CODES: 'actOnStatusCodes',
	FAILURE_NAME: 'alertIntegrationName',
	RESET_FAILURE: 'resetAlertIntegrationName',
	ENABLED: 'enabled',
};

interface FormValues {
	name: string;
	displayName: string;
	integrationName: string;
	events: AutoCompleteOption[];
	retryStrategy: 'none' | 'one' | 'three' | 'exponential';
	failureHandling: string;
	mode: 'none' | 'suspend' | 'divert' | 'divertAndSuspend';
	actOnTimeout: string[];
	actOnStatusCodes: AutoCompleteOption[];
	alertIntegrationName: string;
	resetAlertIntegrationName: boolean;
	enabled: string[];
}

const WEBHOOK_CONFIGURATION_ENABLED_ID = 'enabled';
const WEBHOOK_CONFIGURATION_ACT_ON_TIMEOUT = 'actOnTimeout';
const WEBHOOK_CONFIGURATION_RESET_FAILURE = 'resetAlertIntegrationName';

export default function WebhookConfigurationModal({
	open,
	webhooks,
	onModalClose,
	onSuccess,
	configuredWebhook,
	configuredWebhooks,
	tenantHostname,
}: Props) {
	const [expandFailureHandling, setExpandFailureHandling] =
		useState<boolean>(false);
	const client = useQueryClient();
	const isEdit = !_.isEmpty(configuredWebhook);
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const authTenantId = authData?.tenantAuthorizationData?.tenantId;
	const eventsQuery = useQuery({
		queryKey: [EVENTS_TYPES_QUERY_KEYS.EVENTS_TYPES],
		queryFn: () =>
			tenantHostname
				? TenantsApi.getEventTypes(tenantHostname)
				: getEventTypes(authToken),
	});

	const resolver = yupResolver(
		yup.object().shape({
			[WEBHOOK_CONFIGURATION_FORM_KEYS.NAME]: yup
				.string()
				.matches(/^[a-zA-Z0-9-_]+$/, INVALID_FORMAT_ERROR_MSG())
				.test('name_taken', IS_TAKEN_ERROR_MSG('Name'), (name) => {
					if (isEdit) return true;

					return !configuredWebhooks.map((cwh) => cwh.name).includes(name);
				})
				.required(WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE()),
			[WEBHOOK_CONFIGURATION_FORM_KEYS.DISPLAYNAME]: yup
				.string()
				.matches(
					/^$|^[a-zA-Z0-9-_!*.()~ ]{1,256}$/,
					DISPLAYNAME_INVALID_FORMAT(),
				)
				.test('cleared_after_set', EMPTY_ERROR_MSG(), (displayName) => {
					// fail if displayName is set and then unset
					if (isEdit && configuredWebhook.displayName && isEmpty(displayName)) {
						return false;
					}
					return true;
				}),
			[WEBHOOK_CONFIGURATION_FORM_KEYS.INTEGRATION_NAME]: yup
				.string()
				.nullable()
				.required(WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE()),
			[WEBHOOK_CONFIGURATION_FORM_KEYS.FAILURE_NAME]: yup
				.string()
				.nullable()
				.notOneOf(
					[yup.ref(WEBHOOK_CONFIGURATION_FORM_KEYS.INTEGRATION_NAME)],
					WEBHOOK_INTEGRATION_ERR_MSG(),
				),
			[WEBHOOK_CONFIGURATION_FORM_KEYS.RETRY_STRATEGY]: yup
				.string()
				.nullable()
				.required(WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE()),
		}),
	);

	const { control, watch, handleSubmit, formState, setValue } =
		useForm<FormValues>({
			defaultValues: {
				[WEBHOOK_CONFIGURATION_FORM_KEYS.NAME]: isEdit
					? configuredWebhook.name
					: '',
				[WEBHOOK_CONFIGURATION_FORM_KEYS.DISPLAYNAME]: isEdit
					? configuredWebhook.displayName
					: '',
				[WEBHOOK_CONFIGURATION_FORM_KEYS.INTEGRATION_NAME]: isEdit
					? configuredWebhook.integrationName
					: '',
				[WEBHOOK_CONFIGURATION_FORM_KEYS.EVENTS]: isEdit
					? configuredWebhook.events.map((value) => ({
							id: value,
							label: value,
					  }))
					: [],
				[WEBHOOK_CONFIGURATION_FORM_KEYS.RETRY_STRATEGY]: isEdit
					? configuredWebhook.retryStrategy
					: WEBHOOK_RETRY_STRATEGY_THREE,
				[WEBHOOK_CONFIGURATION_FORM_KEYS.FAILURE_MODE]: isEdit
					? configuredWebhook?.failureHandling?.mode
					: WEBHOOK_FAILURE_MODE_SUSPEND,
				[WEBHOOK_CONFIGURATION_FORM_KEYS.ACT_ON_TIMEOUT]: isEdit
					? [
							...(configuredWebhook.failureHandling.actOnTimeout
								? [WEBHOOK_CONFIGURATION_ACT_ON_TIMEOUT]
								: []),
					  ]
					: [WEBHOOK_CONFIGURATION_ACT_ON_TIMEOUT],
				[WEBHOOK_CONFIGURATION_FORM_KEYS.ACT_ON_CODES]: isEdit
					? configuredWebhook?.failureHandling?.actOnStatusCodes.map(
							(value) => ({
								id: value,
								label: value,
							}),
					  )
					: [],
				[WEBHOOK_CONFIGURATION_FORM_KEYS.FAILURE_NAME]: isEdit
					? configuredWebhook?.alertIntegrationName
					: '',
				[WEBHOOK_CONFIGURATION_FORM_KEYS.RESET_FAILURE]: isEdit
					? [
							...(configuredWebhook.resetAlertIntegrationName
								? [WEBHOOK_CONFIGURATION_RESET_FAILURE]
								: []),
					  ]
					: [WEBHOOK_CONFIGURATION_RESET_FAILURE],
				[WEBHOOK_CONFIGURATION_FORM_KEYS.ENABLED]: isEdit
					? [
							...(configuredWebhook.enabled
								? [WEBHOOK_CONFIGURATION_ENABLED_ID]
								: []),
					  ]
					: [WEBHOOK_CONFIGURATION_ENABLED_ID],
			},
			mode: 'onBlur',
			shouldFocusError: true,
			resolver,
		});

	const { retryStrategy, actOnStatusCodes } = watch();

	useEffect(() => {
		if (retryStrategy === 'none') {
			setValue(
				WEBHOOK_CONFIGURATION_FORM_KEYS.FAILURE_MODE as keyof FormValues,
				'none',
			);
		}
	}, [retryStrategy, setValue]);

	const getRetryStrategyOptions = (): AutoCompleteOption[] => [
		{
			id: WEBHOOK_RETRY_STRATEGY_NONE,
			label: WEBHOOK_RETRY_STRATEGY_NONE,
		},
		{
			id: WEBHOOK_RETRY_STRATEGY_ONE,
			label: WEBHOOK_RETRY_STRATEGY_ONE,
		},
		{
			id: WEBHOOK_RETRY_STRATEGY_THREE,
			label: WEBHOOK_RETRY_STRATEGY_THREE,
		},
		// TODO: add in later once supported.
		// {
		// 	id: WEBHOOK_RETRY_STRATEGY_EXPONENTIAL,
		// 	label: WEBHOOK_RETRY_STRATEGY_EXPONENTIAL,
		// },
	];

	const getFailureHandlingModeOptions = (): AutoCompleteOption[] => {
		let failureMode = [
			{
				id: WEBHOOK_FAILURE_MODE_NONE,
				label: WEBHOOK_FAILURE_MODE_NONE,
			},
			{
				id: WEBHOOK_FAILURE_MODE_SUSPEND,
				label: WEBHOOK_FAILURE_MODE_SUSPEND,
			},
			{
				id: WEBHOOK_FAILURE_MODE_DIVERT,
				label: WEBHOOK_FAILURE_MODE_DIVERT,
			},
			// TODO: add in later once supported.
			// {
			// 	id: WEBHOOK_FAILURE_MODE_DIVERT_AND_SUSPEND,
			// 	label: WEBHOOK_FAILURE_MODE_DIVERT_AND_SUSPEND,
			// },
		];

		return failureMode;
	};

	const getActOnStatusCodeOptions = (): AutoCompleteOption[] => {
		const statusCodeOptions = WEBHOOK_STATUS_CODES.map((sc) => {
			return {
				id: sc,
				label: sc,
			};
		});
		return statusCodeOptions;
	};

	const getIntegrationNameOptions = () => {
		let integrationNameOptions: AutoCompleteOption[] = [
			{
				id: '',
				label: 'None',
			},
		];
		webhooks?.forEach((webhook) => {
			integrationNameOptions.push({
				id: webhook.name,
				label: webhook.name,
			});
		});

		return integrationNameOptions;
	};

	const getEventTypeOptions = (): AutoCompleteOption[] =>
		eventsQuery?.data?.map((eventType: string) => ({
			id: eventType,
			label: eventType,
		})) ?? [];

	const getEnabledOptions = (): AutoCompleteOption[] => [
		{
			id: WEBHOOK_CONFIGURATION_ENABLED_ID,
			label: WEBHOOK_CONFIGURATION_MODAL_ENABLED_LABEL(),
		},
	];

	const getActOnTimeoutOptions = (): AutoCompleteOption[] => [
		{
			id: WEBHOOK_CONFIGURATION_ACT_ON_TIMEOUT,
			label: WEBHOOK_CONFIGURATION_MODAL_ACT_ON_TIMEOUT_LABEL(),
		},
	];

	const handleOnCancel = () => {
		onModalClose();
	};

	const {
		mutate: addUpdateWebhookConfiguration,
		isLoading: isAddingUpdatingWebhookConfiguration,
	} = useMutation(
		(requestPayload: WebhookRequest) => {
			return tenantHostname
				? TenantsApi.configureWebhook(requestPayload, tenantHostname)
				: configureWebhook(requestPayload, authToken);
		},
		{
			onSuccess: async () => {
				await client.invalidateQueries([
					QUERY_KEYS.WEBHOOK_CONFIGURATIONS,
					authTenantId,
				]);
				await onSuccess();
				successToast(
					isEdit
						? WEBHOOK_CONFIGURATION_EDITED_SUCCESS()
						: WEBHOOK_CONFIGURATION_ADDED_SUCCESS(),
				);
			},
			onError: (err) => {
				const genericError = isEdit
					? WEBHOOK_CONFIGURATION_EDITED_FAILURE()
					: WEBHOOK_CONFIGURATION_ADDED_FAILURE();
				if (axios.isAxiosError(err) && err?.response?.data?.message) {
					errorToast(genericError + ': ' + err.response.data.message);
				} else {
					errorToast(genericError);
				}
			},
		},
	);

	const onSubmit: SubmitHandler<FormValues> = (submittedValues) => {
		const requestPayload: WebhookRequest = {
			name: submittedValues?.name,
			displayName: submittedValues?.displayName,
			integrationName: submittedValues?.integrationName,
			retryStrategy: submittedValues?.retryStrategy,
			events: submittedValues?.events.map(({ id }) => id),
			// convert enabled correctly to boolean value
			enabled: submittedValues.enabled[0] === WEBHOOK_CONFIGURATION_ENABLED_ID,
			alertIntegrationName: submittedValues?.alertIntegrationName
				? submittedValues.alertIntegrationName
				: null,
			failureHandling: {
				mode: submittedValues?.mode,
				// convert actOnTimeout correctly to boolean value
				actOnTimeout:
					submittedValues?.actOnTimeout?.[0] ===
					WEBHOOK_CONFIGURATION_ACT_ON_TIMEOUT,
				actOnStatusCodes: submittedValues?.actOnStatusCodes.map(({ id }) => id),
			},
		};
		// if displayName is empty remove it from payload
		if (isEmpty(submittedValues.displayName)) {
			delete requestPayload.displayName;
		}
		// remove failureIntegrationName and set resetAlertIntegrationName to true
		// if failureIntegrationName is cleared (from a previously set value)
		if (
			configuredWebhook &&
			configuredWebhook?.alertIntegrationName &&
			isEmpty(requestPayload.alertIntegrationName)
		) {
			requestPayload.resetAlertIntegrationName = true;
			delete requestPayload.alertIntegrationName;
		}
		addUpdateWebhookConfiguration(requestPayload);
	};

	return (
		<>
			<Dialog
				fullWidth
				open={open}
				onClose={(e, r) => handleModalClose(r, handleOnCancel)}
			>
				<DialogTitle>
					{isEdit
						? EDIT_LABEL(WEBHOOK_CONFIGURATION_LABEL)
						: NEW_LABEL(WEBHOOK_CONFIGURATION_LABEL)}
				</DialogTitle>
				<DialogContent>
					<Stack
						alignItems='flex-start'
						component='form'
						direction='column'
						spacing={2}
						onSubmit={handleSubmit(onSubmit)}
					>
						<TextFieldElement
							name={WEBHOOK_CONFIGURATION_FORM_KEYS.NAME as keyof FormValues}
							fullWidth
							label={WEBHOOK_CONFIGURATION_MODAL_NAME_LABEL()}
							variant='outlined'
							sx={{ backgroundColor: 'white.lightest', mt: 1 }}
							helperText={UNEDITABLE_FIELD_INFO()}
							control={control}
							disabled={isEdit}
							autoFocus={!isEdit}
						/>
						<TextFieldElement
							name={
								WEBHOOK_CONFIGURATION_FORM_KEYS.DISPLAYNAME as keyof FormValues
							}
							fullWidth
							label={WEBHOOK_CONFIGURATION_MODAL_DISPLAYNAME_LABEL()}
							variant='outlined'
							sx={{ backgroundColor: 'white.lightest', mt: 1 }}
							control={control}
						/>
						<SelectElement
							label={WEBHOOK_CONFIGURATION_MODAL_INTEGRATION_NAME_LABEL()}
							name={
								WEBHOOK_CONFIGURATION_FORM_KEYS.INTEGRATION_NAME as keyof FormValues
							}
							options={getIntegrationNameOptions()}
							control={control}
							fullWidth
							variant='outlined'
						/>
						<AutocompleteElement
							multiple
							label={WEBHOOK_CONFIGURATION_MODAL_EVENTS_LABEL()}
							name={WEBHOOK_CONFIGURATION_FORM_KEYS.EVENTS as keyof FormValues}
							options={getEventTypeOptions()}
							control={control}
							autocompleteProps={{
								fullWidth: true,
								limitTags: 2,
							}}
							textFieldProps={{
								variant: 'outlined',
							}}
						/>
						<SelectElement
							label={WEBHOOK_CONFIGURATION_MODAL_ALERT_INTEGRATION_NAME_LABEL()}
							name={
								WEBHOOK_CONFIGURATION_FORM_KEYS.FAILURE_NAME as keyof FormValues
							}
							options={getIntegrationNameOptions()}
							control={control}
							fullWidth
							variant='outlined'
						/>
						<SelectElement
							label={WEBHOOK_CONFIGURATION_MODAL_RETRY_STRATEGY_LABEL()}
							name={
								WEBHOOK_CONFIGURATION_FORM_KEYS.RETRY_STRATEGY as keyof FormValues
							}
							options={getRetryStrategyOptions()}
							control={control}
							fullWidth
							variant='outlined'
						/>
						<Box width={1}>
							<Accordion
								expanded={expandFailureHandling}
								onChange={() =>
									setExpandFailureHandling(!expandFailureHandling)
								}
								sx={{
									width: '100%',
									borderRadius: '4px',
									border: '1px solid rgba(0, 0, 0, 0.25)',
									boxShadow: 'none',
								}}
							>
								<AccordionSummary expandIcon={<ExpandMore />}>
									<Typography>
										{WEBHOOK_CONFIGURATION_MODAL_FAILURE_HANDLING_LABEL()}
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Stack spacing={2}>
										<SelectElement
											label={WEBHOOK_CONFIGURATION_MODAL_FAILURE_STRATEGY_LABEL()}
											name={
												WEBHOOK_CONFIGURATION_FORM_KEYS.FAILURE_MODE as keyof FormValues
											}
											options={getFailureHandlingModeOptions()}
											control={control}
											fullWidth
											disabled={retryStrategy === 'none'}
											variant='outlined'
											helperText={
												retryStrategy === 'none' &&
												WEBHOOK_CONFIGURATION_MODAL_FAILURE_STRATEGY_DISABLED_HELPER_TEXT()
											}
										/>
										<AutocompleteElement
											multiple
											label={WEBHOOK_CONFIGURATION_MODAL_ACT_ON_STATUS_CODE_LABEL()}
											name={
												WEBHOOK_CONFIGURATION_FORM_KEYS.ACT_ON_CODES as keyof FormValues
											}
											options={getActOnStatusCodeOptions()}
											control={control}
											autocompleteProps={{
												fullWidth: true,
												limitTags: 2,
												disabled: retryStrategy === 'none',
											}}
											textFieldProps={{
												variant: 'outlined',
												helperText: !actOnStatusCodes?.length
													? WEBHOOK_ACT_ON_STATUS_MSG()
													: null,
											}}
										/>
										<CheckboxButtonGroup
											name={
												WEBHOOK_CONFIGURATION_FORM_KEYS.ACT_ON_TIMEOUT as keyof FormValues
											}
											control={control}
											disabled={retryStrategy === 'none'}
											options={getActOnTimeoutOptions()}
											row
											checkboxColor='secondary'
										/>
									</Stack>
								</AccordionDetails>
							</Accordion>
						</Box>
						<CheckboxButtonGroup
							name={WEBHOOK_CONFIGURATION_FORM_KEYS.ENABLED as keyof FormValues}
							control={control}
							options={getEnabledOptions()}
							row
							checkboxColor='secondary'
						/>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ m: 2 }}>
					<Button onClick={handleOnCancel} color='neutralButton'>
						{ACTION_CANCEL()}
					</Button>
					<Button
						onClick={handleSubmit(onSubmit)}
						color='secondary'
						variant='contained'
					>
						{!formState.isSubmitting &&
						!isAddingUpdatingWebhookConfiguration ? (
							isEdit ? (
								ACTION_SAVE()
							) : (
								ACTION_CREATE()
							)
						) : (
							<CircularProgress size={20} style={{ color: 'white' }} />
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
